/* ==========================================================================
 * Native header controller — replaces the Alpine components in header.liquid.
 * Owns: bar/header positioning, sticky show-hide on scroll, drawer + cart
 * coordination, wishlist count, and the desktop + mobile search
 * (suggest.json fetch + render). Search<->nav exclusivity runs on DOM events.
 * Alpine stays loaded for the rest of the site; the header no longer uses it.
 * ======================================================================== */
(function () {
  'use strict';

  var header = document.querySelector('[data-section-type="header"]');
  if (!header || header.__headerInit) return;
  header.__headerInit = true;

  // ---------- placement: header section is CSS-sticky; JS only scroll-hides it ----------
  // The header-group class is on every section, so the sticky element is the
  // .shopify-section that wraps this header. We translate THAT to hide it.
  var headerSection = header.closest('.shopify-section') || header;
  var reposition = window.publishHeaderHeight || function () {};
  function hideDistance() { return headerSection.offsetHeight + 10; }

  // ---------- sticky show / hide on scroll ----------
  var state = {
    isVisible: true,
    atPageTop: true,
    lastScrollY: 0,
    drawerOpen: false,
    forceTop: false,
    searchActive: false
  };
  var SCROLL_THRESHOLD = 200;

  function applyVisibility() {
    var hide = !state.isVisible && !state.atPageTop && !state.forceTop;
    headerSection.style.transform = hide ? 'translateY(-' + hideDistance() + 'px)' : '';
    headerSection.style.zIndex = state.drawerOpen ? '2147483646' : state.forceTop ? '30' : '40';
    if (!state.isVisible) closeAllDropdowns();
  }
  function handleScroll() {
    if (state.drawerOpen || state.forceTop) return;
    var y = window.scrollY;
    state.atPageTop = y <= 20;
    if (state.atPageTop) {
      state.isVisible = true;
    } else if (y > SCROLL_THRESHOLD) {
      state.isVisible = y < state.lastScrollY;
    } else {
      state.isVisible = true;
    }
    state.lastScrollY = y;
    applyVisibility();
  }

  // ---------- search / nav exclusivity ----------
  function closeAllDropdowns() {
    window.dispatchEvent(new CustomEvent('header:close-nav'));
    closeDesktopSearch();
  }

  reposition();

  handleScroll();
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { handleScroll(); ticking = false; });
  }, { passive: true });

  var prevMobile = window.innerWidth < 768;
  window.addEventListener('resize', function () {
    var nowMobile = window.innerWidth < 768;
    if (nowMobile !== prevMobile && state.drawerOpen) {
      window.dispatchEvent(new CustomEvent('drawer-menu-close'));
    }
    prevMobile = nowMobile;
    reposition();
  });

  window.addEventListener('load', reposition);

  // ---------- drawer + force-top coordination ----------
  window.addEventListener('drawer-menu-open', function () {
    state.drawerOpen = true; state.isVisible = true; applyVisibility();
  });
  window.addEventListener('drawer-menu-close', function () {
    state.drawerOpen = false; handleScroll();
  });
  window.addEventListener('force-header-top', function () {
    state.forceTop = true; state.isVisible = true; document.body.classList.add('overflow-hidden'); applyVisibility();
  });
  window.addEventListener('restore-header-position', function () {
    state.forceTop = false; document.body.classList.remove('overflow-hidden'); handleScroll();
  });

  // mobile hamburger → toggle drawer
  var drawerToggle = header.querySelector('[data-drawer-toggle]');
  if (drawerToggle) {
    drawerToggle.addEventListener('click', function () {
      window.dispatchEvent(new CustomEvent(state.drawerOpen ? 'drawer-menu-close' : 'drawer-menu-open'));
    });
    window.addEventListener('drawer-menu-open', function () { drawerToggle.classList.add('is-open'); });
    window.addEventListener('drawer-menu-close', function () { drawerToggle.classList.remove('is-open'); });
  }

  // ---------- wishlist count ----------
  var wishCount = header.querySelector('[data-wishlist-count]');
  function renderWish(n) {
    if (!wishCount) return;
    wishCount.textContent = n;
    // Use inline display (beats the permanent Tailwind `flex` class, which would
    // otherwise override the `hidden` attribute and keep an empty/0 badge visible).
    wishCount.hidden = !(n > 0);
    wishCount.style.display = n > 0 ? 'flex' : 'none';
  }
  function readWish() {
    try { return Object.keys(JSON.parse(localStorage.getItem('favorites') || '{}')).length; }
    catch (_) { return 0; }
  }
  renderWish(readWish());
  window.addEventListener('wishlist-updated', function (e) {
    renderWish((e.detail && e.detail.count != null) ? e.detail.count : readWish());
  });

  // ---------- search: shared fetch ----------
  var SUGGEST =
    '/search/suggest.json?resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=hide&q=';
  function fetchSuggest(term) {
    return fetch(SUGGEST + encodeURIComponent(term), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) { return d.resources.results; });
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  }); }
  function q(s) { return encodeURIComponent(s); }

  function renderResults(res, term, opts) {
    opts = opts || {};
    var cols = (res.collections || []).slice(0, opts.colLimit || 4);
    var queries = (res.queries || []).slice(0, opts.qLimit || 3);
    var products = (res.products || []).slice(0, opts.pLimit || 8);
    if (!cols.length && !queries.length && !products.length) {
      return '<div class="py-6 text-center text-gray-500 text-sm">No results found for "' + esc(term) + '"</div>';
    }
    var html = '';
    if (cols.length) {
      html += '<div style="margin-bottom:12px"><p class="text-sm font-bold text-black" style="margin-bottom:6px">Categories</p><div style="display:flex;flex-wrap:wrap;gap:6px">';
      cols.forEach(function (c) {
        html += '<a href="' + esc(c.url) + '" class="text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors" style="display:inline-flex;align-items:center;padding:4px 10px;font-size:12px;border:1px solid #e5e7eb;border-radius:9999px">' + esc(c.title) + '</a>';
      });
      html += '</div></div>';
    }
    if (queries.length) {
      html += '<div class="mb-5"><p class="text-sm font-bold text-black mb-2">Popular</p><div class="space-y-1">';
      queries.forEach(function (qq) {
        html += '<a href="/search?q=' + q(qq.text) + '" class="flex items-center gap-2.5 py-1.5 text-sm text-gray-700 hover:text-black transition-colors">' +
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/></svg>' +
          '<span>' + esc(qq.text) + '</span></a>';
      });
      html += '</div></div>';
    }
    if (products.length) {
      var gridStyle = 'grid-template-columns:repeat(3,1fr);gap:8px';
      html += '<div><p class="text-xs font-bold text-black mb-2">Products</p><div style="display:grid;' + gridStyle + '">';
      products.forEach(function (p) {
        html += '<a href="' + esc(p.url) + '" class="group"><div class="aspect-square bg-gray-100 rounded overflow-hidden mb-0.5">' +
          '<img src="' + esc(p.image || '') + '" alt="' + esc(p.title) + '" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy"></div>' +
          '<p class="text-[9px] text-gray-600 group-hover:text-black line-clamp-2 leading-tight">' + esc(p.title) + '</p></a>';
      });
      html += '</div><div class="mt-3 pt-3 border-t border-gray-100"><a href="/search?q=' + q(term) +
        '" class="flex items-center justify-center gap-1.5 text-sm text-gray-600 hover:text-black transition-colors py-1"><span>View all results</span>' +
        '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg></a></div></div>';
    }
    return html;
  }

  // ---------- desktop search ----------
  var dsWrap = header.querySelector('[data-search]');
  var dsInput = dsWrap && dsWrap.querySelector('[data-search-input]');
  var dsClear = dsWrap && dsWrap.querySelector('[data-search-clear]');
  var dsDropdown = dsWrap && dsWrap.querySelector('[data-search-dropdown]');
  var dsResults = dsWrap && dsWrap.querySelector('[data-search-results]');
  var dsDefault = dsWrap && dsWrap.querySelector('[data-search-default]');
  var dsSpinner = dsWrap && dsWrap.querySelector('[data-search-spinner]');
  var dsTimer;

  function openDesktopSearch() {
    if (!dsWrap) return;
    state.searchActive = true;
    dsWrap.classList.add('is-active');
    if (dsDropdown) dsDropdown.classList.add('is-open');
    window.dispatchEvent(new CustomEvent('header:close-nav'));
  }
  function closeDesktopSearch() {
    if (!dsWrap) return;
    state.searchActive = false;
    dsWrap.classList.remove('is-active');
    if (dsDropdown) dsDropdown.classList.remove('is-open');
  }
  function desktopSearchNow() {
    var term = dsInput.value.trim();
    if (dsClear) dsClear.hidden = term.length === 0;
    if (term.length < 2) {
      if (dsResults) dsResults.innerHTML = '';
      showDefaultPanel(true);
      return;
    }
    clearTimeout(dsTimer);
    dsTimer = setTimeout(function () {
      if (dsSpinner) dsSpinner.hidden = false;
      showDefaultPanel(false);
      fetchSuggest(term).then(function (res) {
        if (dsSpinner) dsSpinner.hidden = true;
        if (dsResults) dsResults.innerHTML = renderResults(res, term, { pLimit: 6 });
      }).catch(function () { if (dsSpinner) dsSpinner.hidden = true; });
    }, 300);
  }
  function showDefaultPanel(show) {
    if (dsDefault) dsDefault.hidden = !show;
    if (dsResults) dsResults.hidden = show;
  }

  if (dsWrap) {
    dsWrap.addEventListener('mouseenter', openDesktopSearch);
    dsWrap.addEventListener('mouseleave', function () {
      if (!document.activeElement || !document.activeElement.closest('[data-search]')) closeDesktopSearch();
    });
    dsInput.addEventListener('focus', openDesktopSearch);
    // collapsed to its icon until .is-active, so a click must reach the input
    var dsBox = dsWrap.querySelector('.search-input-wrapper');
    if (dsBox) dsBox.addEventListener('click', function (e) {
      if (!e.target.closest('[data-search-clear]')) dsInput.focus();
    });
    dsInput.addEventListener('input', desktopSearchNow);
    dsInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeDesktopSearch(); dsInput.blur(); }
      if (e.key === 'Enter' && dsInput.value.trim()) location.href = '/search?q=' + q(dsInput.value.trim());
    });
    // keep focus on the input when clicking a result so blur only fires on a
    // genuine focus-out — no timer needed to outlast the click
    if (dsDropdown) dsDropdown.addEventListener('mousedown', function (e) { e.preventDefault(); });
    dsInput.addEventListener('blur', function (e) {
      if (!dsWrap.contains(e.relatedTarget)) closeDesktopSearch();
    });
    if (dsClear) dsClear.addEventListener('click', function () {
      dsInput.value = ''; dsClear.hidden = true;
      if (dsResults) dsResults.innerHTML = '';
      showDefaultPanel(true); dsInput.focus(); openDesktopSearch();
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-search]')) closeDesktopSearch();
    });
    window.addEventListener('header:close-search', closeDesktopSearch);
    showDefaultPanel(true);
  }

  // ---------- mobile fullscreen search ----------
  var msWrap = document.querySelector('[data-msearch]');
  var msInput = msWrap && msWrap.querySelector('[data-msearch-input]');
  var msResults = msWrap && msWrap.querySelector('[data-msearch-results]');
  var msSpinner = msWrap && msWrap.querySelector('[data-msearch-spinner]');
  var msSuggest = msWrap && msWrap.querySelector('[data-msearch-suggest]');
  var msTimer;

  function msShowSuggest(show) { if (msSuggest) msSuggest.hidden = !show; }

  function openMobileSearch() {
    if (!msWrap) return;
    // Move the overlay to <body> so its position:fixed escapes the header
    // section — a transformed ancestor (the sticky header's scroll-hide
    // translate) would otherwise re-anchor it and drag it away on scroll.
    if (msWrap.parentNode !== document.body) document.body.appendChild(msWrap);
    msWrap.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    window.dispatchEvent(new CustomEvent('force-header-top'));
    if (msInput) requestAnimationFrame(function () { msInput.focus(); });
  }
  function closeMobileSearch() {
    if (!msWrap) return;
    msWrap.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    window.dispatchEvent(new CustomEvent('restore-header-position'));
    if (msInput) msInput.value = '';
    if (msResults) msResults.innerHTML = '';
    msShowSuggest(true);
  }
  function mobileSearchNow() {
    var term = msInput.value.trim();
    if (term.length < 2) { if (msResults) msResults.innerHTML = ''; msShowSuggest(true); return; }
    clearTimeout(msTimer);
    msTimer = setTimeout(function () {
      if (msSpinner) msSpinner.hidden = false;
      msShowSuggest(false);
      fetchSuggest(term).then(function (res) {
        if (msSpinner) msSpinner.hidden = true;
        if (msResults) msResults.innerHTML = renderResults(res, term, { mobile: true, colLimit: 3, pLimit: 6 });
      }).catch(function () { if (msSpinner) msSpinner.hidden = true; });
    }, 300);
  }
  if (msWrap) {
    if (msInput) {
      msInput.addEventListener('focus', openMobileSearch);
      msInput.addEventListener('input', mobileSearchNow);
    }
    window.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeMobileSearch(); });
  }
  // triggers to open/close mobile search (icons/back button)
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-msearch-open]')) { e.preventDefault(); openMobileSearch(); }
    if (e.target.closest('[data-msearch-close]')) { e.preventDefault(); closeMobileSearch(); }
    if (e.target.closest('[data-msearch-clear]')) {
      e.preventDefault();
      if (msInput && msInput.value.trim() === '') { closeMobileSearch(); return; }
      if (msInput) { msInput.value = ''; msInput.focus(); }
      if (msResults) msResults.innerHTML = '';
      msShowSuggest(true);
    }
  });

  // ---------- secondary category row: scroll arrows + edge fades ----------
  (function () {
    var row = header.querySelector('[data-nav-scroll]');
    if (!row) return;
    var list = row.querySelector('.site-nav-list');
    var leftBtn = row.querySelector('[data-nav-arrow="left"]');
    var rightBtn = row.querySelector('[data-nav-arrow="right"]');
    if (!list) return;
    function update() {
      var max = list.scrollWidth - list.clientWidth;
      var canLeft = list.scrollLeft > 2;
      var canRight = list.scrollLeft < max - 2;
      row.classList.toggle('can-scroll-left', canLeft);
      row.classList.toggle('can-scroll-right', canRight);
      if (leftBtn) leftBtn.hidden = !canLeft;
      if (rightBtn) rightBtn.hidden = !canRight;
    }
    list.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    if (leftBtn) leftBtn.addEventListener('click', function () { list.scrollBy({ left: -Math.round(list.clientWidth * 0.7), behavior: 'smooth' }); });
    if (rightBtn) rightBtn.addEventListener('click', function () { list.scrollBy({ left: Math.round(list.clientWidth * 0.7), behavior: 'smooth' }); });
    update();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    window.addEventListener('load', update);
  })();

  // ---------- small helpers ----------

  applyVisibility();
})();
